import React, { useContext, useEffect, useState } from 'react';
import { ProjectsContext } from './provider';
import { FiMoreHorizontal } from 'react-icons/fi';
import { EmptyState } from '@/components/custom/empty-state';
import { Modal } from '@/components/custom/modal/modal';
import moment from 'moment';
import 'moment/locale/pt';
import EventForm from './eventForm';
import { confirm } from '@/lib/confirm';
import toast from 'react-hot-toast';
import { useClient } from 'jsonapi-react';
import usePermission from '@/hooks/usePermission';
import { useTranslation } from 'react-i18next';
import { ProjectWorkspaceWidget, ProjectWorkspaceWidgetHeader, ProjectWorkspaceWidgetTitle } from '@/components/custom/project';
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuContent } from '@/components/ui/dropdown-menu';
import { Schedule, ScheduleDate, ScheduleHeader, ScheduleHour, ScheduleInfos, ScheduleItem, ScheduleTitle, ScheduleWrapper } from '@/components/custom/schedule';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ModalScheduleDate, ModalScheduleHour, ModalScheduleImg, ModalScheduleText } from '@/components/custom/modal';

export function ProjectSchedule() {
  const { t } = useTranslation();
  const client = useClient();
  const { selectedProject, getEvents, events, setEvents, editingEvent, setEditingEvent } = useContext(ProjectsContext);
  const [showFormEvent, setShowFormEvent] = useState(false);
  const [currentEvent, setCurrentEvent] = useState();

  useEffect(() => {
    getEvents();
  }, []);

  const closeFormEvent = () => {
    setShowFormEvent(false);
    setEditingEvent(null);
    getEvents();
  };

  const editEvent = event => {
    setEditingEvent(event);
    setShowFormEvent(true);
  };

  const eventsCopy = structuredClone(events);

  const confirmDeleteEvent = async event => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteEvent'),
      description: t('projects.warningDeleteCannotBeUndoneText'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      deleteEvent(event);
      toast.success(t('toast.successDeleteEvent'));
    }
  };

  const deleteEvent = async event => {
    const { error } = await client.delete(['events', event.id]);
    if (!error) {
      window.location.reload(false);
    } else {
    }
  };

  return (
    <ProjectWorkspaceWidget>
      <ProjectWorkspaceWidgetHeader>
        <ProjectWorkspaceWidgetTitle>{t('projects.titleProjectSchedule')}</ProjectWorkspaceWidgetTitle>
        {!selectedProject?.approved && usePermission('Criar eventos', 'Projetos') ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button>
                <FiMoreHorizontal />
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setShowFormEvent(true)}>{t('projects.newEvent')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </ProjectWorkspaceWidgetHeader>

      <ScheduleWrapper>
        {false && (
          <EmptyState
            type="date"
            title="Novo Evento"
            text={
              <span>
                Ainda não há eventos no projeto. Clique em <FiMoreHorizontal /> ao lado de Cronograma do projeto, para criar novo evento.
              </span>
            }
          />
        )}

        <Schedule>
          {selectedProject?.approved && (
            <ScheduleItem>
              <ScheduleDate variant="success">04/06</ScheduleDate>
              <ScheduleInfos>
                <ScheduleHeader>
                  <ScheduleTitle>Projeto aprovado</ScheduleTitle>
                </ScheduleHeader>
                <ScheduleHour>Dia inteiro</ScheduleHour>
              </ScheduleInfos>
            </ScheduleItem>
          )}
          {eventsCopy.reverse().map((event, index) => (
            <ScheduleItem key={index}>
              <ScheduleDate>{moment(event?.['initial-date']).format('DD/MM')}</ScheduleDate>
              <ScheduleInfos>
                <ScheduleHeader>
                  <ScheduleTitle
                    className="schedule__title"
                    title="Data limite da atividade complementar"
                  >
                    {event.title}
                  </ScheduleTitle>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button>
                        <FiMoreHorizontal />
                      </button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end">
                      {usePermission('Editar eventos', 'Projetos') && !selectedProject?.approved ? <DropdownMenuItem onClick={() => editEvent(event)}>{t('button.edit')}</DropdownMenuItem> : null}
                      {usePermission('Excluir eventos', 'Projetos') && !selectedProject?.approved ? <DropdownMenuItem onClick={() => confirmDeleteEvent(event)}>{t('button.delete')}</DropdownMenuItem> : null}
                      <DropdownMenuItem onClick={() => setCurrentEvent(event)}>{t('button.expand')}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </ScheduleHeader>
                <ScheduleHour>
                  {event['initial-time']} - {event['end-time']}
                </ScheduleHour>
              </ScheduleInfos>
            </ScheduleItem>
          ))}
          <ScheduleItem disabled>
            <ScheduleDate>{moment(selectedProject?.['created-at']).format('DD/MM')}</ScheduleDate>
            <ScheduleInfos>
              <ScheduleHeader>
                <ScheduleTitle>{t('projects.titleCreateProject')}</ScheduleTitle>
              </ScheduleHeader>
              <ScheduleHour>{t('projects.titleFullDay')}</ScheduleHour>
            </ScheduleInfos>
          </ScheduleItem>
        </Schedule>
      </ScheduleWrapper>

      <Modal
        show={showFormEvent}
        onClose={closeFormEvent}
      >
        <EventForm
          onClose={() => setShowFormEvent(false)}
          editingEvent={editingEvent}
          events={events}
          setEvent={setEvents}
          selectedProject={selectedProject}
        />
      </Modal>

      <Dialog
        open={currentEvent}
        onOpenChange={() => setCurrentEvent(null)}
      >
        <DialogContent>
          {currentEvent && (
            <>
              <DialogHeader>
                <DialogTitle>{currentEvent.title}</DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                <ModalScheduleImg
                  src={currentEvent.image?.url}
                  alt="banner evento"
                />

                <ModalScheduleDate>{moment(currentEvent['initial-date']).format(t('datesForm.formatDate'))}</ModalScheduleDate>
                <ModalScheduleHour>
                  {currentEvent['initial-time']} - {currentEvent['end-time']}
                </ModalScheduleHour>

                <ModalScheduleText>{currentEvent.description}</ModalScheduleText>

                {currentEvent.url && (
                  <div>
                    <label htmlFor="linkEvent">Link</label>

                    <div className="flex items-stretch">
                      <Input
                        className="rounded-r-none"
                        id="linkEvent"
                        name="linkEvent"
                        type="url"
                        value={currentEvent.url}
                        readOnly
                        disabled
                      />
                      <Button
                        variant="dark"
                        className="rounded-l-none rounded-r-sm"
                        wide
                        asChild
                      >
                        <a
                          href={currentEvent.url}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          Entrar
                        </a>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </ProjectWorkspaceWidget>
  );
}
